import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { QUIZ_STEPS } from '../data/mock';
import { quizApi } from '../lib/api';
import { useCart } from '../context/CartContext';

const Quiz = () => {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [complete, setComplete] = useState(false);
  const [recs, setRecs] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const { addItem } = useCart();

  const current = QUIZ_STEPS[step];

  const finish = async (finalAnswers) => {
    setSubmitting(true);
    try {
      const data = await quizApi.submit(finalAnswers);
      setRecs(data.recommendations || []);
    } catch (e) {
      console.error(e);
      setRecs([]);
    } finally {
      setSubmitting(false);
      setComplete(true);
    }
  };

  const handleSelect = (value) => {
    const next = { ...answers, [current.key]: value };
    setAnswers(next);
    setTimeout(() => {
      if (step < QUIZ_STEPS.length - 1) setStep(step + 1);
      else finish(next);
    }, 220);
  };

  const handleSkip = () => {
    if (step < QUIZ_STEPS.length - 1) setStep(step + 1);
    else finish(answers);
  };

  const handleBack = () => {
    if (complete) { setComplete(false); return; }
    if (step > 0) setStep(step - 1);
  };

  if (complete) {
    return (
      <main className="bg-[#f5f1e6] text-[#1a1410] min-h-screen px-6 lg:px-10 py-16">
        <div className="max-w-5xl mx-auto">
          <div className="text-[11px] tracking-[0.28em] text-[#3a2e26] border-t border-[#1a1410] pt-3 inline-block">
            YOUR PERSONAL EDIT
          </div>
          <h1 className="font-serif text-[56px] lg:text-[72px] leading-[1.05] mt-6">
            Composed <span className="italic">for you.</span>
          </h1>
          <p className="mt-6 max-w-xl text-[#3a2e26]">Three shades selected from the maison — based on your skin, undertone, finish, and mood.</p>

          {submitting ? (
            <p className="mt-20 text-center text-[#7a6a5a]">Loading…</p>
          ) : (
            <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-12">
              {recs.map((p) => (
                <div key={p.id} className="group">
                  <Link to={`/product/${p.id}`} className="block overflow-hidden bg-[#e9e2d3] aspect-[3/4]">
                    <img src={p.image} alt={p.name} className="w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-[1.04]" />
                  </Link>
                  <div className="mt-5">
                    <div className="text-[10px] tracking-[0.22em] uppercase text-[#7a6a5a]">{p.category}</div>
                    <div className="font-serif text-[24px] mt-1">{p.name}</div>
                    <div className="text-sm text-[#5a4a3e] mt-1">Shade — {p.shade}</div>
                    <div className="font-serif text-[18px] mt-2">Rs. {p.price.toFixed(2)}</div>
                    <button onClick={() => addItem(p)} className="mt-5 w-full bg-[#1a1410] text-[#f5f1e6] py-3 text-[11px] tracking-[0.32em] hover:bg-black transition-colors">
                      ADD TO BAG
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="mt-16 flex items-center gap-8">
            <button onClick={() => { setStep(0); setAnswers({}); setComplete(false); setRecs([]); }} className="text-[11px] tracking-[0.28em] border-b border-[#1a1410] pb-1">
              RETAKE THE QUIZ
            </button>
            <button onClick={() => navigate('/shop')} className="text-[11px] tracking-[0.28em] text-[#7a6a5a] hover:text-[#1a1410]">
              BROWSE ALL SHADES
            </button>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="bg-[#f5f1e6] text-[#1a1410] min-h-screen px-6 lg:px-10 py-16">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-end justify-between">
          <div className="text-[11px] tracking-[0.28em] text-[#7a6a5a] border-t border-[#cdc2b1] pt-3 inline-block">
            STEP {step + 1} OF {QUIZ_STEPS.length}
          </div>
          <div className="text-[11px] tracking-[0.28em] text-[#1a1410] border-t border-[#1a1410] pt-3 inline-block">
            FIND YOUR SHADE
          </div>
        </div>

        <div className="mt-20">
          <div className="text-[11px] tracking-[0.28em] text-[#3a2e26] border-t border-[#1a1410] pt-3 inline-block">
            {current.eyebrow}
          </div>
          <h1 className="font-serif text-[56px] lg:text-[72px] leading-[1.05] mt-6 italic">
            {current.title}
          </h1>

          <div className="mt-16 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-6">
            {current.options.map((opt) => {
              const selected = answers[current.key] === opt.value;
              return (
                <button key={opt.value} onClick={() => handleSelect(opt.value)} className="group text-center">
                  <div className={`aspect-square w-full transition-transform duration-300 ${selected ? 'scale-[1.04] ring-2 ring-[#1a1410] ring-offset-4 ring-offset-[#f5f1e6]' : 'group-hover:scale-[1.03]'}`}
                    style={{ backgroundColor: opt.color }} />
                  <div className="text-[11px] tracking-[0.22em] mt-4 border-t border-[#1a1410] pt-2 inline-block">
                    {opt.label}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="mt-20 flex items-center justify-between">
          <button onClick={handleBack} disabled={step === 0} className="inline-flex items-center gap-2 text-[11px] tracking-[0.28em] disabled:opacity-30">
            <ArrowLeft className="w-4 h-4" strokeWidth={1.4} /> BACK
          </button>
          <button onClick={handleSkip} className="inline-flex items-center gap-2 text-[11px] tracking-[0.28em] text-[#7a6a5a] hover:text-[#1a1410]">
            SKIP <ArrowRight className="w-4 h-4" strokeWidth={1.4} />
          </button>
        </div>
      </div>
    </main>
  );
};

export default Quiz;
